import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `ftue:` scope (F9), following the exact
 * `notifications:`/`web:` precedent (`features/notifications/notifications
 * .i18n.ts`) — NOT a second i18n runtime (E18/§13.3 ban), just physically
 * scoped here because `packages/i18n/**` is outside this task's ownership
 * boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/ftue.json`.
 */
const en = {
  // Space Mono uppercase progress captions — mockup-01 (17-ftue-onboarding.html
  // `FTUE_STEPS`): every step carries its own eyebrow, not just "welcome".
  'eyebrow.welcome': 'Welcome to xDocs',
  'eyebrow.generate': 'Step 1 · Generate',
  'eyebrow.features': 'Step 2 · Confirm & customize',
  'eyebrow.documents': 'Step 3 · Documents',
  'eyebrow.credits': 'Step 4 · Credits',
  'eyebrow.share': "You're all set",
  'step.welcome.title': "Hi, I'm Doc.",
  'step.welcome.body': "I'll show you around in a few seconds. You can skip any time — I won't be offended.",
  'step.generate.title': 'Generate your first document',
  'step.generate.body':
    'Pick a template, then chat with me to shape it — upload your own files or choose from public-domain books.',
  'step.features.title': 'Confirm, then customize',
  'step.features.body':
    "You'll confirm the structure before anything is written. After that: expand chapters, cut things down, or change the reading level — all a click away.",
  'step.documents.title': 'Everything lives in your documents',
  'step.documents.body': 'Browse, organize into folders, and import files from your Google Drive.',
  'step.credits.title': 'Keep an eye on your credits',
  'step.credits.body': 'Every action shows its cost upfront. Your balance is always visible right here.',
  'step.share.title': "You're ready!",
  'step.share.body': 'Publish and share your finished documents with anyone, anytime. Let’s make something.',
  'nav.back': 'Back',
  'nav.next': 'Next',
  /** Primary CTA on the first ("welcome") step only — mockup-01's step-1
   * `cta: 'Show me around'` (every later step's mock `cta` is plain "Next",
   * already covered by `nav.next`). */
  'nav.start': 'Show me around',
  'nav.skip': 'Skip all',
  'nav.finish': 'Start creating',
  'nav.progress': 'Step {current} of {total}',
  'nav.close': 'Close tutorial',
  'error.completeFailed': "Couldn't save that you finished the tour — we'll offer it again next time.",
} as const;

const ro: Record<keyof typeof en, string> = {
  'eyebrow.welcome': 'Bine ai venit la xDocs',
  'eyebrow.generate': 'Pasul 1 · Generează',
  'eyebrow.features': 'Pasul 2 · Confirmă și personalizează',
  'eyebrow.documents': 'Pasul 3 · Documente',
  'eyebrow.credits': 'Pasul 4 · Credite',
  'eyebrow.share': 'Ești gata',
  'step.welcome.title': 'Salut, sunt Doc.',
  'step.welcome.body': 'Te ghidez în câteva secunde. Poți sări peste oricând — nu mă supăr.',
  'step.generate.title': 'Generează primul tău document',
  'step.generate.body':
    'Alege un șablon, apoi discută cu mine ca să-l conturăm — încarcă propriile fișiere sau alege cărți din domeniul public.',
  'step.features.title': 'Confirmă, apoi personalizează',
  'step.features.body':
    'Vei confirma structura înainte să se scrie ceva. După aceea: extinde capitole, scurtează sau schimbă nivelul de lectură — totul la un clic distanță.',
  'step.documents.title': 'Totul e în documentele tale',
  'step.documents.body': 'Răsfoiește, organizează în foldere și importă fișiere din Google Drive.',
  'step.credits.title': 'Ține sub observație creditele',
  'step.credits.body': 'Fiecare acțiune arată costul din start. Soldul tău e mereu vizibil chiar aici.',
  'step.share.title': 'Ești gata!',
  'step.share.body': 'Publică și distribuie documentele finalizate oricui, oricând. Hai să creăm ceva.',
  'nav.back': 'Înapoi',
  'nav.next': 'Următorul',
  'nav.start': 'Arată-mi',
  'nav.skip': 'Sari peste tot',
  'nav.finish': 'Începe să creezi',
  'nav.progress': 'Pasul {current} din {total}',
  'nav.close': 'Închide tutorialul',
  'error.completeFailed': 'Nu am putut salva că ai terminat turul — îl vom oferi din nou data viitoare.',
};

export const FTUE_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type FtueCatalogKey = keyof typeof en;
